import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/layout/Layout';
import Navbar from '../../components/layout/Navbar';
import StatCard from '../../components/common/StatCard';
import Badge from '../../components/common/Badge';
import { getDashboard } from '../../api/superAdminApi';
import { Users, UserCheck, FolderOpen, IndianRupee, Activity, TrendingUp, Clock } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const COLORS = ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

const Dashboard = () => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getDashboard();
      setData(res.data?.data || res.data || {});
    } catch (err) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const counts = data?.counts || {};

  const stats = data ? [
    { title: 'Total Owners',  value: counts.totalOwners  || 0, icon: UserCheck,    color: 'blue',   subtitle: 'Registered owners' },
    { title: 'Total Groups',  value: counts.totalGroups  || 0, icon: FolderOpen,   color: 'purple', subtitle: 'Created groups' },
    { title: 'Total Members', value: counts.totalMembers || 0, icon: Users,        color: 'green',  subtitle: 'Across all groups' },
    { title: 'Active Groups', value: counts.activeGroups || 0, icon: Activity,     color: 'teal',   subtitle: 'Running groups' },
  ] : [];

  const groupStatusData = data ? [
    { name: 'Active',    value: counts.activeGroups    || 0 },
    { name: 'Pending',   value: counts.pendingGroups   || 0 },
    { name: 'Completed', value: counts.completedGroups || 0 },
  ] : [];

  return (
    <Layout>
      <Navbar
        title="Dashboard"
        subtitle={`Overview — ${format(new Date(), 'EEEE, dd MMM yyyy')}`}
        onRefresh={fetchData}
        loading={loading}
      />
      <div className="p-6 space-y-6">

        {/* Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {loading
            ? Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="card animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-1/2 mb-4" />
                  <div className="h-8 bg-gray-200 rounded w-2/3" />
                </div>
              ))
            : stats.map(s => <StatCard key={s.title} {...s} />)
          }
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Bar Chart */}
          <div className="card col-span-2">
            <h2 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
              <TrendingUp size={18} className="text-blue-600" />
              Monthly Collections
            </h2>
            <div className="h-64">
              {loading ? (
                <div className="flex items-center justify-center h-full text-gray-400">Loading chart...</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data?.monthly_collections || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => [`₹${v.toLocaleString('en-IN')}`, 'Amount']} />
                    <Bar dataKey="amount" fill="#2563eb" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Pie Chart */}
          <div className="card">
            <h2 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Activity size={18} className="text-purple-600" />
              Group Status
            </h2>
            <div className="h-64">
              {loading ? (
                <div className="flex items-center justify-center h-full text-gray-400">Loading...</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={groupStatusData} cx="50%" cy="45%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`} labelLine={false} fontSize={11}>
                      {groupStatusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Legend iconSize={10} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        {/* Recent Owners */}
        <div className="card">
          <h2 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Clock size={18} className="text-green-600" />
            Recently Registered Owners
          </h2>
          {loading ? (
            <div className="space-y-3">
              {[1,2,3].map(i => <div key={i} className="h-12 bg-gray-100 rounded-xl animate-pulse" />)}
            </div>
          ) : (data?.recent_owners || []).length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-4">No owners yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead><tr className="text-left text-xs text-gray-500 uppercase">
                  <th className="pb-2 pr-4">Name</th><th className="pb-2 pr-4">Email</th>
                  <th className="pb-2 pr-4">Phone</th><th className="pb-2 pr-4">Joined</th>
                  <th className="pb-2">Status</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-50">
                  {(data.recent_owners || []).map((o, i) => (
                    <tr key={i} className="hover:bg-gray-50 transition-colors">
                      <td className="py-2.5 pr-4 font-medium text-gray-900">{o.name}</td>
                      <td className="py-2.5 pr-4 text-gray-600">{o.email}</td>
                      <td className="py-2.5 pr-4 text-gray-600">{o.phone}</td>
                      <td className="py-2.5 pr-4 text-gray-500 text-xs">{o.createdAt ? format(new Date(o.createdAt), 'dd MMM yyyy') : '—'}</td>
                      <td className="py-2.5"><Badge status={o.is_active ? 'active' : 'inactive'} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
